import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Lock } from 'lucide-react';
import { Customer, eur } from 'types';

export interface StripeCheckoutFormProps {
  amountCents: number;
  paymentIntentId: string;
  customer: Customer;
}

export default function StripeCheckoutForm({ amountCents, paymentIntentId, customer }: StripeCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/result`, // used when the method needs a redirect (3-D Secure, wallets, ...)
        payment_method_data: { billing_details: { name: customer.name, email: customer.email } },
      },
      redirect: 'if_required',
    });

    if (!stripeError) return navigate(`/result?payment_intent=${paymentIntentId}`); // paid or still processing: the result page asks Stripe
    if (stripeError.type === 'card_error') return navigate(`/result?payment_intent=${paymentIntentId}`); // declined: the result page explains why
    setError(stripeError.message || 'Something went wrong. Please check your details and try again.'); // e.g. incomplete card number
    setProcessing(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="bg-white p-4 rounded-xl border border-gray-100">
        <PaymentElement options={{ layout: 'tabs', fields: { billingDetails: { name: 'never', email: 'never' } } }} />
      </div>
      {error && <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      <button type="submit" disabled={!stripe || processing} className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
        <Lock className="w-5 h-5 mr-2" />{processing ? 'Processing payment…' : `Pay ${eur(amountCents)}`}
      </button>
      <p className="text-xs text-gray-500 text-center">Secured by Stripe. We do not store your card information.</p>
    </form>
  );
}
